import React, { useState } from 'react';

import dayjs from 'dayjs';
import Button from 'epn-ui/umd/dist/components/Button';
import DatePickerRange from 'epn-ui/umd/dist/components/DatePicker/Range';
import Radio, { RadioChangeEvent } from 'epn-ui/umd/dist/components/Radio';
import Select from 'epn-ui/umd/dist/components/Select';
import Skeleton from 'epn-ui/umd/dist/components/Skeleton';
import throttle from 'lodash/throttle';

import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';

import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';

import useI18n from '@i18n';

import { ICommonFilterOption, ICommonFilters } from '@pages/statistics/common/interfaces';

import styles from './Currency.module.scss';

dayjs.extend(weekday);
dayjs.extend(localeData);

const Filters: React.FC<ICommonFilters> = ({
    values,
    onChange,
    options,
    loadings,
    onDateRangeChange,
    minMaxDateRange,
    nextPageCreatives,
    nextPagePlacements,
    onSearch,
}) => {
    const { offers, creatives, placements, currencies, dateRanges, subs } = options;
    const { isLoadingOffers, isLoadingCreatives, isLoadingPlacements, isLoadingSubs } = loadings;
    const screens = useBreakpoint();

    const {
        offerIds,
        creativeIds,
        placement,
        currency,
        dateFrom,
        dateTo,
        datePeriod,
        sub,
        sub2,
        sub3,
        sub4,
        sub5,
    } = values;
    const { maxDate, minDate } = minMaxDateRange;
    const { t } = useI18n('Statistic');
    const { t: tCommon } = useI18n('Common');
    const subsArray = [
        {
            key: 'SUB 1',
            name: 'sub',
            value: sub,
            options: subs.sub,
        },
        {
            key: 'SUB 2',
            name: 'sub2',
            value: sub2,
            options: subs.sub2,
        },
        {
            key: 'SUB 3',
            name: 'sub3',
            value: sub3,
            options: subs.sub3,
        },
        {
            key: 'SUB 4',
            name: 'sub4',
            value: sub4,
            options: subs.sub4,
        },
        {
            key: 'SUB 5',
            name: 'sub5',
            value: sub5,
            options: subs.sub5,
        },
    ];

    const [isSubsOpen, setSubOpen] = useState<boolean>(false);

    const getCreativesValue = (selectOptions: ICommonFilterOption[], optionId: string | null) =>
        selectOptions?.find(option => option.value === optionId) || {
            value: -1,
            label: t('All creatives'),
        };
    // todo: look closer in refactoring
    const onScrollBottom = throttle(({ target }, handler) => {
        const isScrolledToBottom = target.scrollHeight - target.scrollTop === target.clientHeight;

        if (isScrolledToBottom) handler();
    }, 100);

    const selectFilter = (inputValue, option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase());

    return (
        <div className="mb-8 xs:p-4 xs:shadow-1 md:p-6 rounded-[6px]">
            <div className="flex flex-wrap gap-2 xs:gap-4 sm:gap-8">
                <Radio.Group
                    onChange={(e: RadioChangeEvent) => onDateRangeChange(e.target.value)}
                    value={datePeriod}
                    buttonStyle="solid"
                    size={screens.xs ? 'small' : 'middle'}
                    block
                    className="w-full sm:w-auto"
                >
                    {dateRanges.map(item => (
                        <Radio.Button key={item.id} value={item.value}>
                            {item.label}
                        </Radio.Button>
                    ))}
                </Radio.Group>
                <DatePickerRange
                    defaultValue={[dayjs(dateFrom), dayjs(dateTo)]}
                    maxDate={dayjs(maxDate)}
                    minDate={dayjs(minDate)}
                    onChange={dates => {
                        if (dates) {
                            const today = new Date();
                            const [from, to] = dates || [today, today];
                            onChange('dateRange', {
                                dateFrom: from,
                                dateTo: to,
                            });
                            onDateRangeChange(null, {
                                dateFrom: dayjs(from).toDate(),
                                dateTo: dayjs(to).toDate(),
                            });
                        } else onDateRangeChange('today');
                    }}
                    size="middle"
                    format="DD.MM.YYYY"
                    value={[dayjs(dateFrom), dayjs(dateTo)]}
                    className="w-full sm:w-auto md:max-w-[240px]"
                />
                <Skeleton loading={isLoadingOffers} className={styles.customSkeleton}>
                    <Radio.Group
                        onChange={(e: RadioChangeEvent) => onChange('currency', e.target.value)}
                        value={currency}
                        buttonStyle="solid"
                        size={screens.xs ? 'small' : 'middle'}
                        block
                        className="w-full xs:max-w-[48%] sm:w-auto"
                    >
                        {currencies.map(item => (
                            <Radio.Button value={item.value} key={item.value}>
                                {item.label}
                            </Radio.Button>
                        ))}
                    </Radio.Group>
                </Skeleton>
                <Select
                    className="w-full xs:max-w-[47%] sm:w-auto md:min-w-[200px]"
                    id="StatisticsOffersSelect"
                    loading={isLoadingOffers}
                    value={offerIds}
                    onChange={value => onChange('offerIds', value)}
                    options={offers}
                    placeholder={t('All offers')}
                    showSearch
                    notFoundContent={tCommon('Nothing found')}
                    filterOption={selectFilter}
                />

                <Select
                    className="w-full xs:max-w-[47%] sm:w-auto sm:min-w-[235px]"
                    id="StatisticsCreativesSelect"
                    options={creatives}
                    value={getCreativesValue(creatives, creativeIds)}
                    onPopupScroll={e => onScrollBottom(e, nextPageCreatives)}
                    onChange={value => onChange('creativeIds', value)}
                    showSearch
                    onSearch={inputValue => onSearch(inputValue)}
                    loading={isLoadingCreatives}
                    placeholder={t('All creatives')}
                    notFoundContent={tCommon('Nothing found')}
                />

                <Select
                    className="w-full xs:max-w-[47%] sm:w-auto sm:min-w-[235px]"
                    id="StatisticsPlacementsSelect"
                    loading={isLoadingPlacements}
                    value={placement}
                    onChange={(_, option) => onChange('placement', option)}
                    options={placements}
                    onPopupScroll={(e: React.SyntheticEvent<Element, Event>) =>
                        onScrollBottom(e, nextPagePlacements)
                    }
                    placeholder={t('All placements')}
                    notFoundContent={tCommon('Nothing found')}
                />

                <Button onClick={() => setSubOpen(!isSubsOpen)}>
                    {t('add SUB')}
                    <FontAwesomeIcon
                        className={`${
                            isSubsOpen ? 'rotate-180' : ''
                        } transition-transform ease ml-5 transform duration-200`}
                        icon={faChevronDown}
                    />
                </Button>
            </div>

            {isSubsOpen && (
                <div className="flex flex-col mt-4 gap-x-[32px] gap-y-[24px] xs:mt-6 sm:flex-row sm:flex-wrap">
                    {subsArray.map(subItem => (
                        <Select
                            allowClear
                            onChange={value => onChange(subItem.name, value)}
                            onClear={() => onChange(subItem.name, { value: null })}
                            loading={isLoadingSubs}
                            value={subItem.value}
                            options={subItem.options || []}
                            placeholder={subItem.key}
                            disabled={!subItem.options}
                            notFoundContent={tCommon('Nothing found')}
                            className="min-w-[15%] sm:min-w-[120px] md:min-w-[150px] lg:min-w-[170px] xl:min-w-[200px]"
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default Filters;
